import { FieldDisplayType } from "../types";
import { APIFieldItem } from "@/pages/settings/types";
import {
  allowsMultipleFiles,
  FILE_FIELD_ANY_TYPE
} from "@/utils/entityFieldsUtils/fileFieldUtils";

export interface OptionItem {
  // number for integer-PK lookups (industries), string for generated PKs (sub-industries).
  id?: number | string;
  name: string;
  isHidden?: boolean;
  // System-seeded: reorderable/hideable but not renamable/deletable.
  isSystem?: boolean;
}

export interface OptionsManagerData {
  options: string[];
  optionItems?: OptionItem[];
  allowUsersToAdd: boolean;
  defaultValue: string;
  checkboxInlineText?: string;
  // Selected parent id for scoped lookups; drives the scoped bulk-update on save.
  selectedParentId?: number | string | null;
}

export interface FieldFormData {
  fieldLabel: string;
  fieldType: FieldDisplayType;
  fieldHelpText: string;
  option1?: string;
  option2?: string;
  minValue?: number;
  maxValue?: number;
  checkboxInlineText?: string;
  supportedFiles?: string;
  allowMultipleFiles?: boolean;
}

export interface FieldFormState {
  formData: FieldFormData;
  optionsManagerData: OptionsManagerData;
  isRequired: boolean;
  showInCreation: boolean;
  showHelpText: boolean;
}

export const initializeFieldFormData = (
  fieldData: APIFieldItem | null,
  isPresetFieldEdit: boolean = false,
  isEditableSystemField: boolean = false
): FieldFormState => {
  if (!fieldData) {
    return {
      formData: {
        fieldLabel: "",
        fieldType: FieldDisplayType.TEXT,
        fieldHelpText: "",
        checkboxInlineText: ""
      },
      optionsManagerData: {
        options: [""],
        allowUsersToAdd: false,
        defaultValue: "",
        checkboxInlineText: ""
      },
      isRequired: false,
      showInCreation: false,
      showHelpText: false
    };
  }

  const initialFormData: FieldFormData = {
    fieldLabel: fieldData.label,
    fieldType: fieldData.displayType,
    fieldHelpText: fieldData.helpText || "",
    checkboxInlineText: fieldData.config?.checkboxInlineText || "",
    minValue: fieldData.config?.minValue
      ? Number(fieldData.config.minValue)
      : undefined,
    maxValue: fieldData.config?.maxValue
      ? Number(fieldData.config.maxValue)
      : undefined
  };

  let initialOptionsData: OptionsManagerData = {
    options: [""],
    allowUsersToAdd: false,
    defaultValue: "unchecked",
    checkboxInlineText: ""
  };

  if (fieldData.config) {
    if (
      fieldData.displayType === FieldDisplayType.SINGLE_SELECT ||
      fieldData.displayType === FieldDisplayType.MULTI_SELECT
    ) {
      let options: string[] = [""];
      let optionItems: OptionItem[] = [];
      if (fieldData.config.options) {
        if (Array.isArray(fieldData.config.options)) {
          options = fieldData.config.options.map((option: any) => {
            if (typeof option === "string") {
              return option;
            } else if (typeof option === "object" && option !== null) {
              return (
                option.name ||
                option.option ||
                option.displayName ||
                String(option)
              );
            }
            return String(option);
          });
          optionItems = fieldData.config.options.map((option: any) => {
            if (typeof option === "string") {
              return { name: option };
            } else if (typeof option === "object" && option !== null) {
              const name =
                option.name ||
                option.option ||
                option.displayName ||
                String(option);
              return option.id !== undefined
                ? { id: option.id, name, isHidden: option.isHidden === true }
                : { name };
            }
            return { name: String(option) };
          });
        } else if (typeof fieldData.config.options === "object") {
          const optionsObj = fieldData.config.options as Record<string, string>;
          options = Object.values(optionsObj);
          optionItems = Object.entries(optionsObj).map(([id, name]) => {
            // Keep non-integer ids (sub-industry string PKs) as-is; parseInt
            // would make them NaN and collide into duplicate `opt-NaN` keys.
            const numericId = Number(id);
            return {
              id: Number.isInteger(numericId) ? numericId : id,
              name
            };
          });
        }
      }
      // Mark existing values system-seeded → read-only; new unsaved values stay editable.
      if (isEditableSystemField && optionItems.length > 0) {
        optionItems = optionItems.map((optionItem) => ({
          ...optionItem,
          isSystem: true
        }));
      }
      initialOptionsData = {
        options: options.length > 0 ? options : [""],
        optionItems: optionItems.length > 0 ? optionItems : undefined,
        allowUsersToAdd: isPresetFieldEdit
          ? true
          : fieldData.config.allowUsersToAdd || false,
        defaultValue: fieldData.config.defaultValue || "none"
      };
    } else if (fieldData.displayType === FieldDisplayType.SWITCH) {
      initialFormData.option1 = fieldData.config.option1 || "";
      initialFormData.option2 = fieldData.config.option2 || "";
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: fieldData.config.defaultValue || "option1"
      };
    } else if (fieldData.displayType === FieldDisplayType.NUMBER) {
      // Number field configuration is already handled in initialFormData above
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: ""
      };
    } else if (fieldData.displayType === FieldDisplayType.CHECKBOX) {
      const checkboxDefaultValue =
        fieldData.config.defaultValue === "none"
          ? "unchecked"
          : fieldData.config.defaultValue || "unchecked";
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: checkboxDefaultValue,
        checkboxInlineText: fieldData.config.checkboxInlineText || ""
      };
    } else if (fieldData.displayType === FieldDisplayType.CURRENCY) {
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: fieldData.config.defaultValue || ""
      };
    } else if (fieldData.displayType === FieldDisplayType.FILE_UPLOAD) {
      initialFormData.supportedFiles =
        fieldData.config.supportedFiles || FILE_FIELD_ANY_TYPE;
      initialFormData.allowMultipleFiles = allowsMultipleFiles(
        fieldData.config
      );
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: ""
      };
    }
  } else {
    if (fieldData.displayType === FieldDisplayType.CHECKBOX) {
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: "unchecked",
        checkboxInlineText: ""
      };
    } else if (fieldData.displayType === FieldDisplayType.CURRENCY) {
      initialOptionsData = {
        options: [],
        allowUsersToAdd: false,
        defaultValue: ""
      };
    }
  }

  return {
    formData: initialFormData,
    optionsManagerData: initialOptionsData,
    isRequired: fieldData.required || false,
    showInCreation: fieldData.showInCreation || false,
    showHelpText: !!fieldData.helpText
  };
};

export const validateFieldForm = (
  formData: FieldFormData,
  optionsManagerData: OptionsManagerData,
  baseFormValid: boolean,
  isPresetFieldEdit: boolean = false
): boolean => {
  let isValid = baseFormValid;

  if (
    formData.fieldType === FieldDisplayType.SINGLE_SELECT ||
    formData.fieldType === FieldDisplayType.MULTI_SELECT
  ) {
    if (!isPresetFieldEdit) {
      const options = optionsManagerData.options || [];
      const hasValidOption = options.some((option) => option.trim() !== "");
      isValid = isValid && hasValidOption;
    }
  }

  if (formData.fieldType === FieldDisplayType.SWITCH) {
    const option1 = formData.option1?.trim() || "";
    const option2 = formData.option2?.trim() || "";
    isValid = isValid && option1 !== "" && option2 !== "";
  }

  if (formData.fieldType === FieldDisplayType.CURRENCY) {
    const defaultValue = optionsManagerData.defaultValue?.trim() || "";
    isValid = isValid && defaultValue !== "";
  }

  return isValid;
};

export const buildFieldConfig = (
  formData: FieldFormData,
  optionsManagerData: OptionsManagerData,
  isWorkflowField: boolean = false,
  // Ids the field's saved options hold, including rows since deleted, which a new row must not take.
  reservedOptionIds: (number | string)[] = []
): any => {
  let config: any = null;

  if (
    formData.fieldType === FieldDisplayType.SINGLE_SELECT ||
    formData.fieldType === FieldDisplayType.MULTI_SELECT
  ) {
    let options: any;

    if (isWorkflowField) {
      // A row the field already has keeps its stored id, so a rename stays the same option.
      const rows = (optionsManagerData.options || [])
        .map((option, index) => ({
          option: option.trim(),
          storedId: optionsManagerData.optionItems?.[index]?.id
        }))
        .filter(({ option }) => option);
      // A new row's generated id must not reuse a stored one, or it would take over that option's answers.
      const usedIds = new Set([
        ...reservedOptionIds.map(String),
        ...rows.flatMap(({ storedId }) =>
          storedId === undefined ? [] : [String(storedId)]
        )
      ]);
      let nextIndex = 1;
      options = rows.map(({ option, storedId }) => {
        if (storedId !== undefined) return { id: storedId, option };
        while (usedIds.has(`option_${nextIndex}`)) nextIndex++;
        const id = `option_${nextIndex}`;
        usedIds.add(id);
        return { id, option };
      });
    } else {
      const optionsObject: Record<string, string> = {};
      (optionsManagerData.options || []).forEach((option) => {
        if (option.trim()) {
          optionsObject[option.trim()] = option;
        }
      });
      options = optionsObject;
    }

    config = {
      options: options,
      allowUsersToAdd: optionsManagerData.allowUsersToAdd || false,
      defaultValue: optionsManagerData.defaultValue || "none"
    };
  } else if (formData.fieldType === FieldDisplayType.SWITCH) {
    config = {
      option1: formData.option1 || "",
      option2: formData.option2 || "",
      defaultValue: optionsManagerData.defaultValue || "option1"
    };
  } else if (formData.fieldType === FieldDisplayType.CHECKBOX) {
    config = {
      defaultValue: optionsManagerData.defaultValue || "unchecked",
      checkboxInlineText:
        formData.checkboxInlineText ||
        optionsManagerData.checkboxInlineText ||
        ""
    };
  } else if (formData.fieldType === FieldDisplayType.CURRENCY) {
    config = {
      defaultValue: optionsManagerData.defaultValue || ""
    };
  } else if (formData.fieldType === FieldDisplayType.NUMBER) {
    config = {
      minValue: formData.minValue,
      maxValue: formData.maxValue
    };
  } else if (formData.fieldType === FieldDisplayType.FILE_UPLOAD) {
    config = {
      supportedFiles: formData.supportedFiles || FILE_FIELD_ANY_TYPE,
      // Decoded through the same helper the read side uses, so the stored
      // value cannot mean one thing on the way in and another on the way
      // out — the control writes a boolean, but a form initialized from an
      // older field can still hold a checkbox token.
      allowMultipleFiles: allowsMultipleFiles(formData)
    };
  }

  return config;
};

export const getDefaultOptionsData = (
  fieldType: FieldDisplayType
): OptionsManagerData => {
  switch (fieldType) {
    case FieldDisplayType.SINGLE_SELECT:
    case FieldDisplayType.MULTI_SELECT:
      return {
        options: [""],
        allowUsersToAdd: false,
        defaultValue: "none"
      };
    case FieldDisplayType.SWITCH:
      return {
        options: [],
        allowUsersToAdd: false,
        defaultValue: "option1"
      };
    case FieldDisplayType.CHECKBOX:
      return {
        options: [],
        allowUsersToAdd: false,
        defaultValue: "unchecked",
        checkboxInlineText: ""
      };
    case FieldDisplayType.CURRENCY:
      return {
        options: [],
        allowUsersToAdd: false,
        defaultValue: ""
      };
    default:
      return {
        options: [],
        allowUsersToAdd: false,
        defaultValue: "none"
      };
  }
};
